/**
 * Table columns a user can show or hide ("Customize Columns"), per screen.
 *
 * The choice is saved on the server per user (GET/PUT/DELETE table-preferences/:screen) as the default
 * columns the user hid (`hiddenColumns`) and the optional columns the user turned on (`addedColumns`).
 * A column with `defaultVisible: false` is optional: it stays off until a user adds it, so columns that
 * most users don't need never show up empty. Everywhere else in the client a choice is simply the list
 * of visible column fields.
 *
 * Each screen lists its columns here, in the order they appear: `field` is the grid column field,
 * `sortKey` the API sort key when it differs from the field. Fixed columns (row selection, actions)
 * are not listed, so they can never be hidden. Keep every entry in sync with the server's
 * TABLE_PREFERENCE_CONFIG.
 *
 * Adding a screen: add its entry to TABLE_SCREENS and TABLE_COLUMN_CONFIG, then use the
 * `useTablePreferences` hook and `OlcCustomizeColumnsModal` on that screen.
 */

export const TABLE_SCREENS = {
  CONTACTS: 'contacts',
};

export const CONTACT_COLUMNS = [
  { field: 'firstName', label: 'Full Name', sortKey: 'name' },
  { field: 'companyName', label: 'Company' },
  { field: 'address1', label: 'Mailing Address' },
  { field: 'addressStatus', label: 'Address Status' },
  { field: 'tags', label: 'Tags' },
  { field: 'lastMailedDate', label: 'Last Mailed Date' },
  { field: 'lastMailedStatus', label: 'Last Mailed Status' },
  { field: 'lastQrScan', label: 'Last QR Scan', defaultVisible: false },
  { field: 'lastPurlVisitedAt', label: 'Last PURL Visit', defaultVisible: false },
  { field: 'createdAt', label: 'Created Date' },
];

export const TABLE_COLUMN_CONFIG = {
  [TABLE_SCREENS.CONTACTS]: {
    columns: CONTACT_COLUMNS,
    minVisibleColumns: 2,
  },
};

const isDefaultColumn = ({ defaultVisible }) => defaultVisible !== false;

/** The column fields shown when nothing is saved. */
export const getDefaultVisibleColumns = (screen) =>
  (TABLE_COLUMN_CONFIG[screen]?.columns || []).filter(isDefaultColumn).map(({ field }) => field);

/**
 * A saved choice cleaned up: hidden columns must be default columns, added columns optional ones,
 * no duplicates, and if too few columns would stay visible it falls back to the default. Mirrors the
 * server's check.
 */
export const sanitizeColumnPreference = (screen, { hiddenColumns, addedColumns } = {}) => {
  const config = TABLE_COLUMN_CONFIG[screen];
  const empty = { hiddenColumns: [], addedColumns: [] };
  if (!config) return empty;

  const pick = (fields, isWanted) =>
    [...new Set(Array.isArray(fields) ? fields : [])].filter((field) =>
      config.columns.some((column) => column.field === field && isWanted(column))
    );
  const hidden = pick(hiddenColumns, isDefaultColumn);
  const added = pick(addedColumns, (column) => !isDefaultColumn(column));
  const visibleCount = getDefaultVisibleColumns(screen).length - hidden.length + added.length;

  return visibleCount >= config.minVisibleColumns
    ? { hiddenColumns: hidden, addedColumns: added }
    : empty;
};

/** The visible column fields (in column order) for a saved choice. */
export const getVisibleColumns = (screen, preference) => {
  const { hiddenColumns, addedColumns } = sanitizeColumnPreference(screen, preference);

  return (TABLE_COLUMN_CONFIG[screen]?.columns || [])
    .filter((column) =>
      isDefaultColumn(column)
        ? !hiddenColumns.includes(column.field)
        : addedColumns.includes(column.field)
    )
    .map(({ field }) => field);
};

/** The saved form of a list of visible column fields. */
export const toColumnPreference = (screen, visibleColumns) => {
  const columns = TABLE_COLUMN_CONFIG[screen]?.columns || [];
  const isVisible = ({ field }) => visibleColumns.includes(field);

  return {
    hiddenColumns: columns
      .filter((column) => isDefaultColumn(column) && !isVisible(column))
      .map(({ field }) => field),
    addedColumns: columns
      .filter((column) => !isDefaultColumn(column) && isVisible(column))
      .map(({ field }) => field),
  };
};

/** True when the visible columns are exactly the default ones. */
export const isDefaultColumnSelection = (screen, visibleColumns) => {
  const defaults = getDefaultVisibleColumns(screen);

  return (
    visibleColumns.length === defaults.length &&
    defaults.every((field) => visibleColumns.includes(field))
  );
};
